import { Button } from '@ordo/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Separator } from '@ordo/ui/separator'
import { toast } from '@ordo/ui/sonner'
import { BackLink } from 'compositions/BackLink'
import { Page } from 'compositions/Page'
import { useCreateSchedule } from 'hooks/api'
import { getApiErrorMessage } from 'hooks/useFetchApi'
import { Save } from 'lucide-react'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router'
import type { Attachment } from 'types/attachments'

import type { Recipient } from '../communications/recipient-selector'
import { type ScheduleFormData, ScheduleFormFields } from './schedule-form-fields'
export function ScheduleNew() {
  const navigate = useNavigate()
  const { perform: createSchedule, loading: saving } = useCreateSchedule()
  const [formData, setFormData] = useState<ScheduleFormData>({
    name: '',
    method: 'EMAIL',
    subject: '',
    templateId: '',
    eventData: '',
    rrule: '',
  })
  const [recipients, setRecipients] = useState<Recipient[]>([])
  const [attachments, setAttachments] = useState<Attachment[]>([])

  const handleFieldChange = useCallback((field: keyof ScheduleFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }, [])

  const handleAddRecipient = useCallback((recipient: Recipient) => {
    setRecipients((prev) => [...prev, recipient])
  }, [])

  const handleRemoveRecipient = useCallback((recipient: Recipient) => {
    setRecipients((prev) => prev.filter((r) => !(r.type === recipient.type && r.id === recipient.id)))
  }, [])

  const canSubmit = formData.name.trim() && formData.rrule && !saving

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()

      let eventData: Record<string, unknown> | undefined
      if (formData.eventData.trim()) {
        try {
          eventData = JSON.parse(formData.eventData)
        } catch {
          return // don't submit with invalid JSON
        }
      }

      try {
        const created = await createSchedule({
          rrule: formData.rrule,
          name: formData.name,
          method: formData.method,
          subject: formData.method === 'EMAIL' ? formData.subject || undefined : undefined,
          templateId: formData.templateId || undefined,
          eventData,
          contactIds: recipients.filter((r) => r.type === 'contact').map((r) => r.id),
          contactGroupIds: recipients.filter((r) => r.type === 'group').map((r) => r.id),
          attachmentIds: attachments.length > 0 ? attachments.map((a) => a.id) : undefined,
        })
        navigate(`/schedule/${created.id}`, { replace: true })
      } catch (err) {
        toast.error('Failed to create schedule', { description: getApiErrorMessage(err) })
      }
    },
    [formData, recipients, attachments, createSchedule, navigate],
  )

  return (
    <Page>
      <div className="mb-4">
        <BackLink to="/schedule">Back to Schedules</BackLink>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
            <CardTitle>New Schedule</CardTitle>
            <Button type="submit" size="sm" disabled={!canSubmit}>
              <Save className="mr-1 h-4 w-4" />
              {saving ? 'Creating...' : 'Create Schedule'}
            </Button>
          </CardHeader>

          <Separator />

          <CardContent className="pt-6">
            <ScheduleFormFields
              data={formData}
              onChange={handleFieldChange}
              recipients={recipients}
              onAddRecipient={handleAddRecipient}
              onRemoveRecipient={handleRemoveRecipient}
              attachments={attachments}
              onAttachmentsChange={setAttachments}
            />
          </CardContent>
        </Card>
      </form>
    </Page>
  )
}
